import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { commissionsSettingsRanksApi } from "../../../../modules/commissions/ranks/api";
import { CreateUpdateCommissionsSettingsRank } from "../../../../modules/commissions/ranks/types";
import { EnumerationType } from "../../../../modules/enumerations/enums";
import type { UUID } from "../../../../typings/global";
import { EntityObject, SearchPageRequest } from "../../../types";
import { invalidateEnumeration } from "../enumerations";
import { COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS } from "./queryKeys";

export const useFilterCommissionsSettingsRanks = (filter: SearchPageRequest) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.filterCommissionsSettingsRanks(filter),
    queryFn: async () => {
      const { data } = await commissionsSettingsRanksApi.filterCommissionsSettingsRanks(filter);

      return data;
    },
  });
};

export const useCreateCommissionsSettingsRanks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.createCommissionsSettingsRanks(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCommissionsSettingsRank) => {
      const { data } = await commissionsSettingsRanksApi.createCommissionsSettingsRanks(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.filterCommissionsSettingsRanks(),
      });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionsSettingsRanks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.updateCommissionsSettingsRanks(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCommissionsSettingsRank>) => {
      const { data } = await commissionsSettingsRanksApi.updateCommissionsSettingsRanks(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.filterCommissionsSettingsRanks(),
      });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionsSettingsRanks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.deleteCommissionsSettingsRanks(),
    mutationFn: async (id: UUID) => {
      return commissionsSettingsRanksApi.deleteCommissionsSettingsRanks({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_RANKS_QUERY_KEYS.filterCommissionsSettingsRanks(),
      });
      Notification.itemDeletedNotification();
    },
  });
};
